/**
 * Node type metadata for the canvas + chat reference chips.
 *
 * Mirrors design/kaiva-canvas/project/components/script.jsx#TYPE_META.
 * Single source of truth for type label, accent color, and icon —
 * consumed by canvas nodes (Phase 4) and chat RefChips (Phase 3.2).
 */

import { Box, Lightbulb, User, Wrench, Zap, type LucideIcon } from 'lucide-react';

export type NodeType = 'step' | 'actor' | 'tool' | 'insight' | 'trigger';

export type TypeMetaEntry = {
  label: string;
  /** HSL string in CSS form, e.g. "221 64% 38%" — wrap in hsl() at call site. */
  color: string;
  /** Lucide icon component. */
  icon: LucideIcon;
};

export const TYPE_META: Record<NodeType, TypeMetaEntry> = {
  step:    { label: 'Step',    color: '213 64% 38%', icon: Box },
  actor:   { label: 'Actor',   color: '258 36% 50%', icon: User },
  tool:    { label: 'Tool',    color: '178 38% 36%', icon: Wrench },
  insight: { label: 'Insight', color: '38 76% 44%',  icon: Lightbulb },
  trigger: { label: 'Trigger', color: '22 100% 46%', icon: Zap },
};

export function isNodeType(value: string): value is NodeType {
  return value === 'step' || value === 'actor' || value === 'tool' || value === 'insight' || value === 'trigger';
}

/**
 * Forgiving lookup — accepts any case (`STEP`, `step`, `Step`) and returns
 * `null` for unknown values so call sites can fall back gracefully.
 */
export function getTypeMeta(value: string | undefined | null): TypeMetaEntry | null {
  if (!value) return null;
  const lower = value.toLowerCase();
  return isNodeType(lower) ? TYPE_META[lower] : null;
}

/** Lowercase + validate. Returns null if not one of the five node types. */
export function normalizeNodeType(value: string | undefined | null): NodeType | null {
  if (!value) return null;
  const lower = value.toLowerCase();
  return isNodeType(lower) ? lower : null;
}
